/**
 * Parses a search URL and extracts search parameters.
 * Handles both one-way and round-trip URL formats, including open-ticket variants:
 * - One-way:       /search/{origin}/{dest}/{departure}/p/{passengers}/...
 * - Round-trip:    /search/{origin}/{dest}/{departure}/{return}/p/{passengers}/...
 * - Open-ticket:   /search/{origin}/{dest}/{departure}/p/{passengers}/departures/open
 *                  /search/open-ticket/{origin}/{dest}/{departure}/{return}/
 * - Open return:   ...?returnIsOpenTicket=true
 *
 * @param {string} pathname - URL pathname
 * @param {string} [queryString] - URL query string (e.g. "?returnIsOpenTicket=true")
 * @returns {{ originSlug: string, destinationSlug: string, departureDate: string, returnDate: string|null, passengersStr: string|null, isDepartureOpen: boolean, isReturnOpen: boolean } | null}
 */
function parseSearchUrl(pathname, queryString = '') {
  const parts = pathname.split('/').filter(Boolean);

  if (parts[0] !== 'search' || parts.length < 4) return null;

  let isDepartureOpen = false;
  let isReturnOpen = false;
  let offset = 1;

  // /search/open-ticket/origin/dest/... route
  const isOpenTicketRoute = parts[1] === 'open-ticket';
  if (isOpenTicketRoute) {
    isDepartureOpen = true;
    offset = 2;
    if (parts.length < 5) return null;
  }

  const originSlug = parts[offset];
  const destinationSlug = parts[offset + 1];
  const departureDate = parts[offset + 2];
  const rest = parts.slice(offset + 3);

  let returnDate = null;
  let passengersStr = null;

  if (isOpenTicketRoute) {
    // /search/open-ticket/{origin}/{dest}/{departure}/{return}/ — no 'p'/passengers
    // marker in this shape at all; whatever follows the departure date is the return date.
    // A simple open-ticket URL has nothing after the departure date (`rest` is empty), so
    // default to null rather than leaving `returnDate` undefined — downstream code (e.g.
    // useTrackSearchChanged.js) compares it against null to detect "no return leg."
    [returnDate = null] = rest;
  } else if (!rest.length) {
    // Nothing after the departure date at all — no 'p'/passengers marker. Every non-open-
    // ticket route in App.js requires one, so this isn't a recognized results shape.
    return null;
  } else if (rest[0] !== 'p') {
    // rest: [returnDate, 'p', passengers, ...] — anything before the 'p' marker is the
    // return-date segment, valid or not (callers validate the value themselves). The 'p'
    // marker itself must still be there, right after it — otherwise the path doesn't
    // actually match the known URL shape.
    if (rest[1] !== 'p') return null;
    [returnDate, , passengersStr] = rest;
  } else {
    // rest: ['p', passengers, ...]
    [, passengersStr] = rest;
  }

  // /departures/open suffix in the regular route
  if (parts.includes('open')) {
    isDepartureOpen = true;
  }

  // Return open ticket via query param
  if (queryString && new URLSearchParams(queryString).get('returnIsOpenTicket') === 'true') {
    isReturnOpen = true;
  }

  return {
    originSlug,
    destinationSlug,
    departureDate,
    returnDate,
    passengersStr,
    isDepartureOpen,
    isReturnOpen,
  };
}

export default parseSearchUrl;
